import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Ticket, ArrowRight, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Microsoft sign-in for the portal. The redirect URI below
// (https://<your-site>/portal/login) must be added to the Entra app registration
// as a Single-page application redirect URI.
const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID || 'common'}`,
    redirectUri: window.location.origin + '/portal/login',
    postLogoutRedirectUri: window.location.origin + '/portal/login',
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

const loginRequest = { scopes: ['openid', 'profile', 'email'] };

let msalInstance = null;
if (msalConfig.auth.clientId) {
  msalInstance = new PublicClientApplication(msalConfig);
}

const PortalLoginPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [msalReady, setMsalReady] = useState(false);
  const [authConfig, setAuthConfig] = useState({ password_login: false, password_registration: false });
  const [form, setForm] = useState({ email: '', password: '', full_name: '' });

  const finishLogin = (data, message) => {
    localStorage.setItem('portal_token', data.access_token);
    localStorage.setItem('portal_user', JSON.stringify(data.user));
    toast.success(message);
    navigate('/portal');
  };

  useEffect(() => {
    api.portalAuthConfig()
      .then((res) => setAuthConfig(res.data))
      .catch(() => setAuthConfig({ password_login: false, password_registration: false }));
  }, []);

  useEffect(() => {
    const init = async () => {
      if (!msalInstance) return;
      try {
        await msalInstance.initialize();
        setMsalReady(true);
        const response = await msalInstance.handleRedirectPromise();
        if (response && response.idToken) {
          setSsoLoading(true);
          const res = await api.portalSSO(response.idToken);
          finishLogin(res.data, 'Welcome!');
        }
      } catch (err) {
        console.error('Portal Microsoft sign-in error:', err);
        toast.error(err.response?.data?.detail || 'Microsoft sign-in failed');
      } finally {
        setSsoLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMicrosoftLogin = async () => {
    if (!msalInstance || !msalReady) {
      toast.error('Microsoft sign-in is not configured. Contact IT.');
      return;
    }
    setSsoLoading(true);
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (err) {
      setSsoLoading(false);
      toast.error(`Microsoft sign-in failed: ${err.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let res;
      if (mode === 'login') {
        res = await api.portalLogin(form.email, form.password);
      } else {
        if (!form.full_name.trim()) {
          toast.error('Full name is required');
          setLoading(false);
          return;
        }
        res = await api.portalRegister(form.email, form.password, form.full_name);
      }
      finishLogin(res.data, mode === 'login' ? 'Welcome back!' : 'Account created!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const showPasswordForm = authConfig.password_login || (mode === 'register' && authConfig.password_registration);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-900 text-white mb-2">
            <Ticket className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">IT Support Portal</h1>
          <p className="text-sm text-slate-500">Submit and track your IT support requests</p>
        </div>

        <Card className="border-slate-200 shadow-lg" data-testid="portal-login-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">{mode === 'login' ? 'Sign In' : 'Create Account'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              type="button"
              onClick={handleMicrosoftLogin}
              className="w-full gap-2 bg-slate-900 hover:bg-slate-800"
              disabled={ssoLoading}
              data-testid="portal-microsoft-btn"
            >
              {ssoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
              Sign in with Microsoft
            </Button>

            {showPasswordForm && (
              <>
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <div className="h-px flex-1 bg-slate-200" />or<div className="h-px flex-1 bg-slate-200" />
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {mode === 'register' && (
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Full Name</label>
                      <Input
                        value={form.full_name}
                        onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                        placeholder="John Doe"
                        data-testid="portal-fullname-input"
                      />
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@gamerpackaging.com"
                      required
                      data-testid="portal-email-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Password</label>
                    <Input
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="Enter your password"
                      required
                      data-testid="portal-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="outline"
                    className="w-full gap-2"
                    disabled={loading}
                    data-testid="portal-submit-btn"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                    {mode === 'login' ? 'Sign in with password' : 'Create Account'}
                  </Button>
                </form>
              </>
            )}

            {authConfig.password_registration && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                  className="text-sm text-slate-500 hover:text-slate-900 transition-colors"
                  data-testid="portal-toggle-mode"
                >
                  {mode === 'login' ? "Don't have an account? Register" : 'Already have an account? Sign In'}
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center">
          <a href="/login" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
            IT Staff Login
          </a>
        </div>
      </div>
    </div>
  );
};

export default PortalLoginPage;
